// Product related models - converted from ActionScript

import { ImageInfo } from './user.model';

export interface Category {
  id: number;
  ref: string;
  name: string;
  image?: ImageInfo;
  /** product_types.brand_id from the API — matches brand-scoped tags to home tabs. */
  brandId?: number | null;
  selected?: boolean;
}

export interface SubCategory {
  id: number;
  name: string;
  ref: string;
  description?: string;
  image?: ImageInfo;
  products?: ProductInfo[];
  selected?: boolean;
}

export interface TipInfo {
  id: number;
  title: string;
  content: string;
  videoUrl?: string;
  image?: ImageInfo;
}

/**
 * CoverInfo - represents an album cover returned by ProductsService.getCovers()
 * 
 * In the legacy app, covers are separate products with typeId=9.
 * When an album is selected, getCovers(subTypeId, albumId) is called to get available covers.
 * Each cover has its own finish options (loaded via getProductFinishesOptions(coverId))
 * and cover-specific panels (loaded via getOptions(panelType, 2, coverId)).
 * 
 * The cover finish option type 7 (Revestimento) is special - when user selects a color,
 * EditorService.getProductFinish(coverId, finishId) is called to get the backgroundImage
 * which shows the actual cover with that color/material.
 */
export interface CoverInfo {
  id: number;
  typeId: number;           // Always 9 (Cover type)
  subTypeId?: number;
  name: string;
  description?: string;
  ref: string;
  image?: ImageInfo;        // Default cover image
  backgroundImage?: string; // Dynamic image that changes based on selected finish (Revestimento)
  price?: number;
  enabled?: boolean;
  selected?: boolean;
  finishOptions?: FinishOptionInfo[];
  finishRules?: FinishRuleInfo[];
  editorTypeId?: number;    // Editor type for covers that support editor (EditorType.COVERS = 2)
  minFiles?: number;        // Minimum required files for cover (0 = no files required)
  maxFiles?: number;        // Maximum allowed files for cover
  allowFiles?: boolean;     // Whether direct file upload is allowed (null/false = not allowed)
  threeDPreview?: boolean;     // API '3dPreview': whether this cover supports the 3D mockup preview
  threeDPreviewType?: number;  // API '3dPreviewType': 1=default spine, 2=front wraps, 3=dual, 4=bind-zero, 5=rounded, 6=wrap variant (type-2 clone for now); 0/undefined=none
  template?: string;           // Downloadable design template URL (drives the "Download Template" link)
}

export interface ProductStockInfo {
  id: number;
  available: boolean;
  message?: string;
}

export interface ProductPriceInfo {
  id: number;
  price: number;
  quantity: number;
  currency: string;
}

/**
 * Detailed price info returned from ProductsService.getProductPrice API
 * Each entry in the prices[] array contains billing details for a component
 */
export interface ProductPriceDetailInfo {
  billingProductId: string;
  ref: string;
  qty: number;
  unitQty: number;
  price: string;
  extraPagePrice?: string;
  discount1?: number | string;
  discount2?: number | string;
  discount3?: number | string;
  discount4?: number | string;
  discount5?: number | string;
  discount6?: number | string;
  discountId?: number;
  vatTab?: string;
  vat?: string;
  incVat?: string;
  netPrice: string;
  code?: string;
  unit?: string;
  weight?: string;
  width?: string;
  height?: string;
  length?: string;
  vouchers?: any[];
  voucherDiscountValue?: string;
  availableStock?: string;
  orderedStock?: string;
  stockOff?: string;
  discountsNotAllowed?: string;
  productionDays?: string;
  productLine?: string;
  productType?: string;
  productBrand?: string;
  productModel?: string;
  productionStartType?: string;
  productionStartHour?: string;
  recargoTax?: string;
}

export interface FormatModel {
  id: number;
  name: string;
  width: number;
  height: number;
  dpi: number;
  /** Per-side material thickness in cm (e.g. cover-board thickness). The
   *  spine-thickness formula uses `coverThickness × 2`. May be 0 / missing
   *  on legacy product records — consumers fall back to a default. */
  thickness?: number;
}

export interface FinishOptionInfo {
  id: number;
  name: string;
  type: number;
  required: boolean;
  enabled: boolean;
  selected?: boolean;
  selectedFinish?: FinishInfo;
  selectedFinishes?: FinishInfo[];
  finishes: FinishInfo[];
  subCategories?: FinishSubCategory[]; // Sub-categories for tabbed dropdown (type 7)
  value?: any;
  min?: number;
  max?: number;
  step?: number;  // Step value for numeric inputs
  files?: any[];
  filesValid?: boolean;
  constrainFiles?: boolean;  // For CHECK_FILES (type 9): when checked, enforces min/max on file count
  filesTypeId?: number;  // File type restriction: 0=all, 1=jpg/png/psd, 2=ai/pdf/eps, 3=pdf only
  note?: string;  // Optional note/hint text for the option (used in TEXT_INPUT, etc.)
  includeInOrder?: number;  // 0 = don't include finish in order (just files), 1 = include finish in order (default)
  tip?: TipInfo; // Tip information with optional image
}

export interface FinishSubCategory {
  id: number;
  name: string;
}

/**
 * Rule types for finish compatibility rules
 * BLOCK (0): When source finish is selected, target is disabled/blocked
 * REQUIRE (1): When source finish is selected, target is required/auto-selected
 */
export enum FinishRuleType {
  BLOCK = 0,
  REQUIRE = 1,
  RANGE = 2  // Sets min/max range for numeric inputs (WIDTH_INPUT, HEIGHT_INPUT)
}

/**
 * FinishRuleInfo - defines compatibility rules between finishes
 * Rules are evaluated when a finish is selected to determine which other finishes
 * should be blocked or required.
 * 
 * Example: If Finish A in Option X is selected, then:
 * - type=0 (BLOCK): Finish B in Option Y becomes disabled
 * - type=1 (REQUIRE): Finish B in Option Y becomes required/auto-selected
 * - type=2 (RANGE): Option Y's min/max values are constrained to rule's min/max
 * 
 * If targetFinishId=0, the rule applies to the entire target option (all finishes blocked/one required)
 */
export interface FinishRuleInfo {
  id: number;
  finishOptionId: number;      // Source finish option ID
  finishId: number;            // Source finish ID (when this finish is selected...)
  targetFinishOptionId: number; // Target finish option ID
  targetFinishId: number;       // Target finish ID (0 = entire option, specific ID = specific finish)
  type: FinishRuleType;         // 0 = BLOCK, 1 = REQUIRE, 2 = RANGE
  min: number;                  // For RANGE rules: minimum value allowed
  max: number;                  // For RANGE rules: maximum value allowed
}

export interface FinishInfo {
  id: number;
  name: string;
  ref: string;
  _explicitType?: string; // AMF type discriminator (e.g., OverlayFinishInfo)
  description?: string;
  image?: ImageInfo;
  overlay?: boolean; // True when finish provides an overlay image (used by cover panels)
  price?: number;
  selected?: boolean;
  enabled?: boolean;
  stockLimited?: number; // 1 = limited stock (shows an out-of-stock warning badge on the swatch)
  maxFiles?: number;
  subTypeId?: number; // Links finish to a sub-category for tabbed dropdown
  color?: string; // Color value for COLOR_PICKER type (hex or name)
  type?: number; // 0=normal, 5=FilesFinishInfo (requires file upload)
  // File constraint properties (from FilesFinishInfo)
  constrainFiles?: number; // 1 = requires file upload when selected
  filesTypeId?: number; // 0=all, 1=jpg/png/psd, 2=ai/pdf/eps, 3=pdf only
  minFiles?: number; // Minimum number of files required
  files?: any[]; // Uploaded files for this finish
  tip?: TipInfo; // Tip information with optional image
  // Paper finish properties (from PaperFinishInfo)
  thickness?: number; // Paper thickness in cm (e.g., 0.1 for 800gr paper)
  weight?: number; // Paper weight
  minPages?: number; // Minimum number of pages
  maxPages?: number; // Maximum number of pages
}

export interface ProductInfo {
  id: number;
  typeId: number;
  subTypeId?: number;
  subTypeName?: string;
  /** REST /products/search embeds the sub-type (with its image) — used as image fallback. */
  subType?: { id?: number; name?: string; image?: ImageInfo };
  sectorId?: number;
  name: string;
  description?: string;
  note?: string;
  ref: string;
  image?: ImageInfo;
  template?: string;
  productUrl?: string;
  finishOptions?: FinishOptionInfo[];
  finishRules?: any[];
  qty: number;
  stepQty: number;
  minQty: number;
  showcase: number;
  tip?: TipInfo;
  editorTypeId: number;
  allowPreview: number;
  allowThemes: number;
  forceTheme: number;
  isDefaultEditor: boolean;
  format?: FormatModel;
  importing?: boolean;
  filesAsProductModel?: boolean;
  filesToImport?: number;
  files: any[];
  projectInfo?: any;
  minFiles: number;
  maxFiles: number;
  ruleMinFiles?: number;
  ruleMaxFiles?: number;
  numFiles: number;
  numPages: number;
  extraPages: number;
  width: number;
  height: number;
  warning?: string;
  prices?: ProductPriceInfo[];
  price?: number;
  stock?: ProductStockInfo;
  images?: ImageInfo[];
  selected?: boolean;
  enabled: boolean;
  showcaseEnabled?: number;  // Server indicates if showcase option is available for this product
  ordercode?: string;
  text?: string;
  optionsFilesValid?: boolean;
  optionsValid?: boolean;
  filesValid?: boolean;
  filesReferenceValid?: boolean;
  ignoreInvalidFiles?: boolean;
  allowInvalidFiles?: boolean;
  allowFiles?: boolean;
  allowFilesQty?: boolean;
  /** Shared page Format id (product_pages.page_id of the product's base page).
   *  Hydrated ONLY on /products/albums and /products/album-enabled-options
   *  (backend 7572eeb); all size clones of the same page geometry share it.
   *  Null when the product has no product_pages rows. */
  pageId?: number | null;
  /** The pages row behind pageId — width/height in cm; id always equals pageId.
   *  Same hydration scope and null cases as pageId. */
  pageFormat?: { id: number; width: number; height: number } | null;
}

export enum ProductType {
  // DreambooksPro categories
  ALBUM = 1,
  ALBUM_TRANSPARENTSHEET = 27,
  BOXES = 13,
  PARENTS = 11,
  PROOFS = 12,
  DVDS = 10,
  GUEST = 16,
  STUDIOBOX = 17,
  BOOKBOX = 34,
  SIGNATURES = 39,
  SHOWCASES = 52,
  SHOWCASE_COVERS = 54,
  ANALOG_ALBUNS = 93,
  PLAFOND = 61,
  
  // OnelabPro categories
  GIFT = 2,
  PHOTOS_POSTER = 3,
  DECOR = 4,
  CALENDAR = 5,
  GREETINGS = 7,
  OFFSET = 8,
  RIGIDS = 28,
  FLEXIBLES = 29,
  CARDS = 58,
  FRAMES = 59,
  FILMROLL = 95,
  SHOWCASE_ONELAB = 60,
  PHOTOS_BR = 99,
  
  // LFMPro categories  
  CAMERAS = 72,
  LENSES = 73,
  FLASHES = 74,
  STUDIO_EQUIPMENT = 75,
  BAGS = 76,
  TRIPODS = 77,
  MEMORY_CARDS = 78,
  PRINTERS = 79,
  VIDEO = 80,
  COMPUTING = 81,
  MOBILE = 82,
  DRONES = 83,
  FRAMES_ALBUNS = 84,
  BATTERIES = 85,
  PAPERS = 86,
  OUTLET = 87,
  GIFTBOXES = 91,
  LFMPRO_CAMPAIGNS = 94
}

// Editor types (from legacy EditorType.as)
export enum EditorType {
  NONE = 0,
  ALBUNS = 1,
  COVERS = 2,
  CALENDARS = 3,
  CANVAS = 4,   // Decoração
  GIFTS = 5,    // Diversos
  PHOTOS = 6
}

// FinishOptionType from legacy FinishOptionType.as
export enum FinishOptionType {
  DROPDOWNLIST = 1,
  DROPDOWNLIST_IMAGES = 2,
  RADIO = 3,
  CHECK = 4,
  LIST = 5,
  NUMERIC_STEPPER = 5,  // Same as LIST
  NUMERIC_LIST = 6,
  TABBED_DROPDOWN_IMAGES = 7,
  DROPDOWN_IMAGES = 8,
  CHECK_FILES = 9,
  WIDTH_INPUT = 10,
  HEIGHT_INPUT = 11,
  TEXT_INPUT = 12,
  CHECK_DVDSOCKET = 13,
  MULTI_SELECT = 14,
  COLOR_PICKER = 15,
  REMOVE_LOGO = 16,
  ENHANCE_FILES = 17,
  MARGINS = 18
}

export enum FileType {
  IMAGE = 1,
  PDF = 2,
  TIFF = 3,
  EPS = 4,
  AI = 5,
  PSD = 6
}

export enum FormType {
  STANDARD = 1,
  CUSTOM = 2
}

/**
 * OptionsPanelInfo - represents a panel of options returned by ProductsService.getOptions()
 * This is used for add-on panels like Stamping, Laser, UV printing, etc.
 * Each panel can contain products (with their own finishOptions) or subcategories.
 */
export interface OptionsPanelInfo {
  name: string;
  ref?: string;            // PanelOption.ref from the API — kept for diagnostics and future validation
  description?: string;
  type: number | string;  // 1 = LIST view, 2 = THUMBS view
  options: ProductInfo[];  // Products available in this panel
  required?: boolean;
  selectNoneLabel?: string;  // Label for "no selection" option (e.g., "Não quero stamping")
  selectNone?: boolean;  // Whether no selection is allowed
  showSingleOption?: boolean;
  multiselect?: boolean;
  note?: string;  // Warning/note text to display
  allowQty?: boolean;
  displayPrice?: boolean;
  displayFilesInside?: boolean;
  displayFinishesOptionsInside?: boolean;
  filesType?: number;
  filesTypeRule?: number;
  filesTitle?: string;
  forceAllowFiles?: boolean;
  requireMaxFiles?: boolean;
  ownerProductId?: number;  // The parent product ID this panel belongs to

  // Client-side tracking
  selectedOption?: ProductInfo | null;  // Currently selected product in this panel
  panelTypeId?: number;  // PanelType this panel was loaded as. Source of truth for
                         // panel-object lookups — the name-keyed panelTypeIds map
                         // collides when two panels share a translated name
                         // (e.g. cover vs box "Personalizações", types 18 vs 19).
}

/**
 * Panel types for product add-on panels (like Stamping, Laser, UV printing, etc.)
 * These are used with ProductsService.getOptions(panelTypeId, formType, productId, ...)
 * to load additional panels that can be added to a product
 */
export enum PanelType {
  // Cover panels (for main albums)
  COVER_EMBOSSING = 14,     // Stamping da Capa
  COVER_LASER = 15,         // Gravação Laser da Capa (usually empty)
  COVER_APPLICATION = 18,   // Personalizações (Chapa)
  COVER_UV = 57,            // Impressão da Capa (UV)
  COVER_EXTRA = 62,         // Extra panel (usually empty)
  
  // Album panels (using album productId, not coverId)
  ALBUM_TRANSPARENT_SHEET = 27, // Folha Papel Vegetal
  ALBUM_BOXES = 13,             // Mala / Box
  ALBUM_REPLICAS = 11,          // Álbuns Réplica (product selection panel)
  ALBUM_PROOFS = 12,            // Álbum de Provas (product selection panel)
  ALBUM_ACCESSORIES = 10,       // Acessórios (product selection panel)
  
  // Box panels (for embalagens/boxes)
  BOX_APPLICATION = 19,
  BOX_LASER = 20,
  BOX_EMBOSSING = 30,
  BOX_UV = 63,
  
  // Album Replica (Parents) panels
  PARENTS_LASER = 21,         // Gravação a Laser da Réplica (API returns this name for type 21)
  PARENTS_BOX = 22,           // Box do Álbum Réplica
  PARENTS_STAMPING = 31,      // Stamping da Réplica
  PARENTS_UV = 64,            // Impressão UV Álbuns Réplica
  
  // Album Provas (Proofs) panels
  PROOFS_STAMPING = 32,       // Stamping do Álbum de Provas
  PROOFS_LASER = 25,          // Gravação a Laser do Álbum de Provas
  PROOFS_UV = 66,             // Impressão UV Album Provas
  
  // Acessórios (DVDs - type 10) panels
  DVDS_STAMPING = 33,         // Stamping for DVDs/accessories (returns empty for most)
  DVDS_LASER = 26,            // Gravação a Laser
  DVDS_UV = 67,               // Impressão UV
  DVDS_PEN = 40,              // Pen Drive
  
  // Box child panels (loaded when a box product is selected)
  BOX_STAMPING = 53,          // Stamping da Box da Réplica
  BOX_UV_REPLICA = 65,        // Impressão UV Box Album Replica
  
  // Studio Box / Print Box panels
  STUDIOBOX_STAMPING = 42,    // Stamping da Print Box
  STUDIOBOX_LASER = 43,       // Gravação a Laser Print Box (may return empty)
  STUDIOBOX_UV = 68,          // Impressão UV Print Box
  STUDIOBOX_PENS = 44,        // Pen Drive (product selection panel)
  STUDIOBOX_EXTRA = 45,       // Extra panel (may return empty)
  
  // Folios (Christmas Folio & co. — products under ProductType.GUEST)
  FOLIOS_EMBOSSING = 96,      // Stamping Folios (guest_book_embossing)
  FOLIOS_LASER = 97,          // Gravação Laser Folios (guest_book_laser)
  FOLIOS_UV = 98,             // Impressão UV Folios (guest_book_uv)

  // Other panels
  FILES_BACKUP = 46,
  BACKUP = 90,                // Backup dos ficheiros de impressão
  
  // Album pages panels
  ALBUM_PAGES = 41
}

/**
 * Mapping of product types to their associated panel types
 * When a product of a certain type is selected, these panels should be loaded
 */
export const PRODUCT_PANELS_MAP: { [productType: number]: PanelType[] } = {
  // Boxes - stamping, application, laser, UV
  [ProductType.BOXES]: [
    PanelType.BOX_EMBOSSING,
    PanelType.BOX_APPLICATION,
    PanelType.BOX_LASER,
    PanelType.BOX_UV,
    PanelType.STUDIOBOX_PENS,
    PanelType.BACKUP              // 90 - Backup dos ficheiros de impressão
  ],
  
  // Albums - Complex flow with multiple panel groups:
  // 1. Album-level panels (using album productId)
  // 2. Cover selection + cover-level panels (using coverId) - handled separately via COVER_PANELS
  // 3. Sub-product selection panels (Replicas, Proofs, Accessories)
  // 
  // Legacy flow: getAlbuns → getCovers → getOptions(27,13) → getProductFinishesOptions(albumId)
  //              → getProductFinishesOptions(coverId) → getOptions(62,14,18,15,57 for cover)
  //              → getOptions(11,12,10 for sub-products)
  [ProductType.ALBUM]: [
    // Album-level panels (first, using album productId)
    PanelType.ALBUM_TRANSPARENT_SHEET, // 27 - Folha Papel Vegetal
    PanelType.ALBUM_BOXES,             // 13 - Mala / Box
    // Sub-product panels (after cover panels)
    PanelType.ALBUM_REPLICAS,          // 11 - Álbuns Réplica
    PanelType.ALBUM_PROOFS,            // 12 - Álbum de Provas
    PanelType.ALBUM_ACCESSORIES,       // 10 - Acessórios
    PanelType.BACKUP                   // 90 - Backup dos ficheiros de impressão (last, after accessories)
  ],
  
  // Analog Albums - use cover panels (stamping and UV printing)
  [ProductType.ANALOG_ALBUNS]: [
    PanelType.COVER_EMBOSSING,  // 14 - Stamping da Capa
    PanelType.COVER_UV          // 57 - Impressão da Capa (UV)
  ],
  
  // Studiobox / Print Box - stamping, laser, UV, pen drive, backup
  // Order based on legacy app trace: 42 (Stamping), 43 (Laser), 68 (UV), 44 (Pen), 45 (Extra), 90 (Backup)
  [ProductType.STUDIOBOX]: [
    PanelType.STUDIOBOX_STAMPING, // 42 - Stamping da Print Box
    PanelType.STUDIOBOX_LASER,    // 43 - Gravação a Laser (may return empty)
    PanelType.STUDIOBOX_UV,       // 68 - Impressão UV Print Box
    PanelType.STUDIOBOX_PENS,     // 44 - Pen Drive (product selection panel)
    PanelType.STUDIOBOX_EXTRA,    // 45 - Extra panel (may return empty)
    PanelType.BACKUP              // 90 - Backup dos ficheiros de impressão
  ],
  
  // BookBox
  [ProductType.BOOKBOX]: [
    PanelType.BOX_EMBOSSING,
    PanelType.BOX_APPLICATION,
    PanelType.BOX_LASER,
    PanelType.BOX_UV,
    PanelType.BACKUP              // 90 - Backup dos ficheiros de impressão
  ],
  
  // Parents albums (replica albums) - uses specific panel types
  // Order: Laser, Stamping, UV printing, then Box (with child panels)
  [ProductType.PARENTS]: [
    PanelType.PARENTS_LASER,      // 21 - Gravação a Laser da Réplica (first)
    PanelType.PARENTS_STAMPING,   // 31 - Stamping da Réplica (second)
    PanelType.PARENTS_UV,         // 64 - Impressão UV Álbuns Réplica (third)
    PanelType.PARENTS_BOX,        // 22 - Box do Álbum Réplica (last - has child panels 53, 65)
    PanelType.BACKUP              // 90 - Backup dos ficheiros de impressão
  ],
  
  // Proofs albums (álbum de provas)
  // Order: Stamping, UV printing, then Backup
  [ProductType.PROOFS]: [
    PanelType.PROOFS_LASER,
    PanelType.PROOFS_STAMPING,    // 32 - Stamping do Álbum de Provas
    PanelType.PROOFS_UV,          // 66 - Impressão UV Album Provas
    PanelType.BACKUP              // 90 - Backup dos ficheiros de impressão
  ],
  
  // DVDs / Acessórios - Laser, UV, Pen Drive
  // Order based on legacy app: Stamping (33 - usually empty), Laser (26), UV (67), Pen (40)
  [ProductType.DVDS]: [
    PanelType.DVDS_STAMPING,      // 33 - Stamping (usually returns empty)
    PanelType.DVDS_LASER,         // 26 - Gravação a Laser
    PanelType.DVDS_UV,            // 67 - Impressão UV
    PanelType.DVDS_PEN,
    PanelType.BACKUP              // 90 - Backup dos ficheiros de impressão
  ],
  
  // Guest albums / Folios (Christmas Folios share typeId 16 — panels the
  // API returns empty for a given product are dropped automatically)
  [ProductType.GUEST]: [
    PanelType.COVER_EMBOSSING,
    PanelType.COVER_LASER,
    PanelType.COVER_APPLICATION,
    PanelType.COVER_UV,
    PanelType.FOLIOS_EMBOSSING,   // 96 - Stamping Folios
    PanelType.FOLIOS_LASER,       // 97 - Gravação Laser Folios
    PanelType.FOLIOS_UV,          // 98 - Impressão UV Folios
    PanelType.BACKUP              // 90 - Backup dos ficheiros de impressão
  ],
  
  // Signatures
  [ProductType.SIGNATURES]: [
    PanelType.COVER_EMBOSSING,
    PanelType.COVER_LASER,
    PanelType.COVER_APPLICATION,
    PanelType.COVER_UV
  ],
  
  // Showcase covers (capas mostruário)
  [ProductType.SHOWCASE_COVERS]: [
    PanelType.COVER_EMBOSSING,
    PanelType.COVER_LASER,
    PanelType.COVER_APPLICATION,
    PanelType.COVER_UV
  ],
  
  // Showcases (mostruários)
  [ProductType.SHOWCASES]: [
    PanelType.COVER_EMBOSSING,
    PanelType.COVER_LASER,
    PanelType.COVER_APPLICATION,
    PanelType.COVER_UV
  ]
};

/**
 * Child panels that are loaded when a product is selected in a parent panel.
 * For example, when a Box is selected in PARENTS_BOX (22), these child panels are loaded
 * using the selected Box product's ID (not the main album's ID).
 * 
 * Key: Parent panel type ID
 * Value: Array of child panel type IDs to load when a product is selected
 */
export const PANEL_CHILD_PANELS_MAP: { [parentPanelType: number]: PanelType[] } = {
  // Box do Álbum Réplica (22) -> when a box is selected, load Stamping (53) and UV (65)
  [PanelType.PARENTS_BOX]: [
    PanelType.BOX_STAMPING,     // 53 - Stamping da Box da Réplica
    PanelType.BOX_UV_REPLICA    // 65 - Impressão UV Box Album Replica
  ],
  // Mala / Box (13) -> when a box is selected, load Stamping (30), Laser (20), UV (63)
  [PanelType.ALBUM_BOXES]: [
    PanelType.BOX_EMBOSSING,    // 30 - Stamping da Box
    PanelType.BOX_LASER,        // 20 - Gravação Laser da Box
    PanelType.BOX_UV,           // 63 - Impressão UV da Box
    PanelType.BOX_APPLICATION,  // 19 - Personalizações (Chapa)
    PanelType.STUDIOBOX_PENS    // 44 - Pen Drive (Studio Box)
  ],
  // Álbuns Réplica (11) -> when selected, load Laser, Stamping, UV, and Box panels
  [PanelType.ALBUM_REPLICAS]: [
    PanelType.PARENTS_LASER,      // 21 - Gravação a Laser da Réplica (parent_book_laser)
    PanelType.PARENTS_STAMPING,   // 31 - Stamping da Réplica (parent_emboss)
    PanelType.PARENTS_UV,         // 64 - Impressão UV Álbuns Réplica
    PanelType.PARENTS_BOX,        // 22 - Box do Álbum Réplica (has its own child panels)
  ],
  // Álbum de Provas (12) -> when selected, load stamping, laser, UV, and backup panels
  [PanelType.ALBUM_PROOFS]: [
    PanelType.PROOFS_STAMPING,    // 32 - Stamping do Álbum de Provas
    PanelType.PROOFS_LASER,       // 25 - Gravação a Laser do Álbum de Provas
    PanelType.PROOFS_UV,          // 66 - Impressão UV Album Provas
  ],
  // Acessórios (10) -> when selected, load stamping, laser, UV, and pen panels
  [PanelType.ALBUM_ACCESSORIES]: [
    PanelType.DVDS_STAMPING,      // 33 - Stamping
    PanelType.DVDS_LASER,         // 26 - Gravação a Laser
    PanelType.DVDS_UV,            // 67 - Impressão UV
    PanelType.DVDS_PEN            // 40 - Pen Drive
  ]
};

/** Maximum number of personalization types selectable at once (cover or
 *  standalone). The selector enforces this; selecting beyond it is blocked. */
export const MAX_PERSONALIZATION_TYPES = 2;

/**
 * Child panels that are PERSONALIZATION types (should appear in the child panel selection box).
 * These are panels that represent personalization options like Stamping, Laser, UV, etc.
 * Other child panels (like Pen Drive, Box) are considered PRODUCT types and appear as regular panels.
 */
export const CHILD_PANEL_PERSONALIZATION_TYPES: PanelType[] = [
  // Box-related personalizations
  PanelType.BOX_STAMPING,       // 53 - Stamping da Box da Réplica
  PanelType.BOX_UV_REPLICA,     // 65 - Impressão UV Box Album Replica
  PanelType.BOX_EMBOSSING,      // 30 - Stamping da Box
  PanelType.BOX_LASER,          // 20 - Gravação Laser da Box
  PanelType.BOX_UV,             // 63 - Impressão UV da Box
  PanelType.BOX_APPLICATION,    // 19 - Personalizações (Chapa)
  
  // Parents book personalizations
  PanelType.PARENTS_LASER,      // 21 - Gravação a Laser da Réplica
  PanelType.PARENTS_STAMPING,   // 31 - Stamping da Réplica
  PanelType.PARENTS_UV,         // 64 - Impressão UV Álbuns Réplica
  
  // Proofs personalizations
  PanelType.PROOFS_STAMPING,    // 32 - Stamping do Álbum de Provas
  PanelType.PROOFS_LASER,       // 25 - Gravação a Laser do Álbum de Provas
  PanelType.PROOFS_UV,          // 66 - Impressão UV Album Provas
  
  // DVDs/Accessories personalizations
  PanelType.DVDS_STAMPING,      // 33 - Stamping
  PanelType.DVDS_LASER,         // 26 - Gravação a Laser
  PanelType.DVDS_UV,            // 67 - Impressão UV
  
  // StudioBox personalizations
  PanelType.STUDIOBOX_STAMPING, // 42 - Stamping
  PanelType.STUDIOBOX_LASER,    // 43 - Gravação a Laser
  PanelType.STUDIOBOX_UV,       // 68 - Impressão UV
  
  // Cover personalizations
  PanelType.COVER_EMBOSSING,    // 14 - Stamping da Capa
  PanelType.COVER_LASER,        // 15 - Gravação Laser da Capa
  PanelType.COVER_APPLICATION,  // 18 - Personalizações (Chapa)
  PanelType.COVER_UV,           // 57 - Impressão da Capa (UV)

  // Folios personalizations (Christmas Folio & co. — join the cover types
  // in the standalone "até 2" selector instead of standalone panels)
  PanelType.FOLIOS_EMBOSSING,   // 96 - Stamping Folios
  PanelType.FOLIOS_LASER,       // 97 - Gravação Laser Folios
  PanelType.FOLIOS_UV           // 98 - Impressão UV Folios
];

/**
 * Panel types that behave like product selection (not just add-on finish options).
 * These panels show products in a grid, and when selected, the product's finish options
 * are loaded via getProductFinishesOptions() API call.
 * 
 * For these panels:
 * - Products are displayed as selectable cards with images
 * - Selecting a product loads its finishOptions and finishRules from API
 * - The finish options are displayed below the product grid
 * - Child panels (if any) are loaded after the product finish options
 */
export const PRODUCT_SELECTION_PANELS: PanelType[] = [
  PanelType.PARENTS_BOX,        // 22 - Box do Álbum Réplica - user selects a box type, then configures its finishes
  PanelType.DVDS_PEN,           // 40 - Pen Drive (DVDs/Acessórios) - user selects a pen type, then configures its finishes
  PanelType.STUDIOBOX_PENS,     // 44 - Pen Drive (Studio Box) - user selects a pen type, then configures its finishes
  PanelType.ALBUM_BOXES,        // 13 - Mala / Box - user selects a box, then configures its finishes
  PanelType.ALBUM_REPLICAS,     // 11 - Álbuns Réplica - user selects a replica album, then configures its finishes
  PanelType.ALBUM_PROOFS,       // 12 - Álbum de Provas - user selects a proofs album, then configures its finishes
  PanelType.ALBUM_ACCESSORIES,  // 10 - Acessórios - user selects an accessory, then configures its finishes
];

/**
 * Panel types that should use getProducts() instead of getOptions().
 * These panels load products independently (not linked to a parent product).
 * 
 * AMF: ProductsService.getProducts(typeId, formType, locale, user, fillFinishOptions)
 * vs
 * AMF: ProductsService.getOptions(typeId, formType, productId, locale, user)
 */
export const PANELS_USING_GET_PRODUCTS: PanelType[] = [
  PanelType.BACKUP,  // 90 - Backup dos ficheiros de impressão
];

/**
 * Cover panel types - these panels are loaded using coverId (not album productId)
 * After selecting a cover (via getCovers()), these panels are loaded using the selected cover's ID.
 * 
 * Legacy flow: getCovers(subTypeId, productId) → user selects cover → 
 *              getProductFinishesOptions(coverId) → getOptions(panelType, 2, coverId)
 */
export const COVER_PANELS: PanelType[] = [
  PanelType.COVER_EXTRA,        // 62 - Extra panel (usually empty)
  PanelType.COVER_EMBOSSING,    // 14 - Stamping da Capa
  PanelType.COVER_APPLICATION,  // 18 - Personalizações (Chapa)
  PanelType.COVER_LASER,        // 15 - Gravação Laser da Capa (usually empty)
  PanelType.COVER_UV            // 57 - Impressão da Capa (UV)
];

// Cover panel types that can provide overlay images for the cover preview.
// Future additions should only require appending a PanelType here.
export const COVER_PANEL_OVERLAY_TYPES: PanelType[] = [
  PanelType.COVER_UV,
  PanelType.COVER_LASER,
  PanelType.COVER_APPLICATION
];

// Finish option (ref `replica_order_id`) that asks the customer for the
// order number of the main album. Hardcoded by id so the dispatcher can
// swap the generic TEXT_INPUT for a curated order picker dialog. Promote
// to a real FinishOptionType when the backend serves a dedicated type id.
export const REPLICA_ORDER_FINISH_OPTION_ID = 154;

// ========== DRAFT MODELS ==========

/**
 * Represents an uploaded file for a finish option
 */
export interface DraftFinishFile {
  id?: number; // Photo DB primary key from upload response
  name: string;
  path: string; // serverName from photoInfo
  /** Top-level folder the file was imported from (folder filter chips). */
  sourceFolder?: string;
  size: number;
  mimeType?: string;
  qty?: number;
  thumbUrl?: string; // Thumbnail data URL (local base64 or server thumb URL)
  url?: string; // Full-size image URL from server
  /** Mid-size server variant URL (picked from the upload response's sizes[])
   *  — feeds the direct file-album preview at reasonable weight. Absolute,
   *  so guests (who cannot resolve serverName → URL) can load it. */
  previewUrl?: string;
  md5?: string; // MD5 hash of the file
  // Server file info for constructing full URLs via PathService
  serverId?: number;
  pathStructureId?: number;
  metadata?: {
    width?: number;
    height?: number;
    dpi?: number | null;
    colorProfile?: string | null;
    fileSize?: number;
    sizeValid?: boolean | null;
  };
}

/**
 * Represents finish values for a product, panel, or cover
 */
export interface DraftFinishValues {
  [optionId: number]: any; // Can be finishId, color value, text, array of finishIds, etc.
}

/**
 * Represents finish names for display (stored alongside finish values to avoid API calls)
 * Maps optionId to the option name and selected value name
 */
export interface DraftFinishNames {
  [optionId: number]: {
    optionName: string;  // e.g., "Nº Folhas", "Acabamento"
    valueName: string;   // e.g., "24 folhas", "Capa Dura", or "Red, Blue" for multi-select
    /** Index of the option in the product's finishOptions array (the FORM
     *  display order) — detail dialogs sort by it; absent on old drafts. */
    order?: number;
  };
}

/**
 * Represents uploaded files for finishes that require file uploads
 */
export interface DraftFinishFiles {
  [key: string]: DraftFinishFile[]; // Key: "panelName-optionId-finishId" or "main-optionId-finishId"
}

/**
 * Represents a product selection within a panel
 */
export interface DraftPanelProduct {
  productId: number;
  productName?: string;
  finishValues?: DraftFinishValues;
  finishNames?: DraftFinishNames;
  finishFiles?: DraftFinishFiles;
}

/**
 * Represents panel configuration (add-ons like Stamping, UV, Box, etc.)
 */
export interface DraftPanelConfig {
  panelName: string;
  panelTypeId: number;
  isOptedOut: boolean;
  selectedProduct?: DraftPanelProduct; // For panels that select a product (like Box)
  finishValues?: DraftFinishValues; // For panels with direct finish options
  finishNames?: DraftFinishNames; // Finish names for display
  finishFiles?: DraftFinishFiles;
  /**
   * FinishOptionType per optionId of the panel's options. Persisted so the
   * GUEST preview (which can't reach getOptions) can discriminate template
   * galleries (7/8) from plates (2) for chapa/corner overlay composition.
   */
  optionTypes?: Record<number, number>;
  // Panel project data (for panels with editor projects like "Folha Papel Vegetal")
  projectId?: number;
  projectServerName?: string;
  projectServerId?: number;
  projectPathStructureId?: number;
  projectThumbnails?: string[]; // Page thumbnails from the editor
  /** Panel project's server lastModificationDate at snapshot time (parity stamp). */
  projectLastModified?: string;
}

/**
 * Represents cover configuration
 */
export interface DraftCoverConfig {
  coverId: number;
  coverName?: string;
  coverRef?: string;
  threeDPreview?: boolean;      // persisted snapshot of cover.threeDPreview (backend 3dPreview)
  threeDPreviewType?: number;   // persisted snapshot of cover.threeDPreviewType (1=default,2=wrap,3=dual,4=bind-zero,5=rounded,6=wrap variant; 0/undefined=none)
  finishValues: DraftFinishValues;
  finishNames?: DraftFinishNames;
  finishFiles?: DraftFinishFiles;
  panels?: DraftPanelConfig[];
}

/**
 * Represents album configuration
 */
export interface DraftAlbumConfig {
  coverCollectionId: number;
  coverCollectionName?: string;
  albumFormatId: number;
  albumFormatName?: string;
  albumProductId: number;
  albumProductName?: string;
  albumPagesFinishValues: DraftFinishValues;
  albumPagesFinishNames?: DraftFinishNames;
  albumPagesFinishFiles?: DraftFinishFiles;
  uploadedPhotos?: DraftFinishFile[]; // Album page photos
  cover: DraftCoverConfig;
  panels?: DraftPanelConfig[]; // Album panels (Box, Provas, DVDs, etc.)
  // Child panel personalization selection state
  // Key: panelTypeId (as string) for draftVersion >= 2, or panelName for legacy drafts
  selectedChildPanelTypes?: { [panelTypeIdOrName: string]: number[] | number | null };
  childPanelsOptedOut?: { [panelTypeIdOrName: string]: boolean };
  // Cover personalization selection state
  selectedCoverPersonalizationType?: number | null;
  /** Multi-select cover personalization types (max 2). Supersedes the singular
   *  field above (kept for backward-compat with older drafts). */
  selectedCoverPersonalizationTypes?: number[];
  coverPersonalizationsOptedOut?: boolean;
  // Order name for project naming
  orderName?: string;
  // Project associations - links editor projects to this order
  coverProjectId?: number;
  coverProjectServerName?: string;
  coverProjectServerId?: number;
  coverProjectPathStructureId?: number;
  // Album project (for album pages)
  albumProjectId?: number;
  albumProjectServerName?: string;
  albumProjectServerId?: number;
  albumProjectPathStructureId?: number;
  // Album page thumbnails (base64 data URLs)
  albumPageThumbnails?: string[];
  // Composed cover thumbnail (base64 data URL) — flat composite of material +
  // design + personalizations, embedded in the .order for the production
  // label script (docs/cover-thumbnail-order-contract.md)
  coverThumbnail?: string;
  // Total album page count (may differ from thumbnails if not all pages were visited)
  albumProjectPageCount?: number;
  /** Album project's server lastModificationDate at snapshot time — the
   *  parity stamp (AlbumProjectParityService): equal to the live project's
   *  stamp means the draft's page count/thumbnails can be trusted; different
   *  or absent triggers a deep re-count from the .dbproject. */
  albumProjectLastModified?: string;
  /** Cover project's server lastModificationDate at snapshot time (parity
   *  stamp) — a mismatch invalidates the baked transparent thumbnail. */
  coverProjectLastModified?: string;
  /** Real single-face format in cm (getProductFormat, cached at upload
   *  validation). The direct file-album preview keys the book's aspect on
   *  it — the commercial product name ("30x30") can differ from the real
   *  cm (e.g. 30.5×25.4), and guests can't call the format API. */
  albumFormatFaceCm?: { width: number; height: number };
  // Transparent thumbnail for cover project (allows finish color to show through)
  coverProjectTransparentThumbnail?: string;
  // Position data for transparent thumbnail (percentages relative to full cover preview)
  coverProjectThumbnailPosition?: { left: number; top: number; width: number; height: number; bleed?: { left: number; right: number; top: number; bottom: number } };
  // Spine width used when cover project was saved (for bind=0 covers)
  // Ensures schematic overlay matches the saved thumbnail
  coverProjectSpineWidth?: number;
  // Bind-zero cover layout (frontWidth, frontHeight, bleed, spineGap,
  // currentSpineWidth) snapshotted at save time. Lets the orders + guest
  // preview paths apply the bleed-aware front/back/spine crop without
  // calling the JWT-gated `getProduct` endpoint (which guests can't
  // reach). Populated only for bind-zero covers; absent for hardcover.
  coverBindZeroLayout?: {
    frontWidth: number;
    frontHeight: number;
    spineGap: number;
    currentSpineWidth: number;
    bleed: { top: number; bottom: number; left: number; right: number };
  };
  // Storytelling chapter data (photo groupings + pagination config)
  storytellingData?: DraftStorytellingData;
  // Storytelling work-in-progress chapters (saved but not yet applied to album)
  storytellingWip?: DraftStorytellingChapter[];
}

/**
 * Serializable chapter data for storytelling draft persistence.
 */
export interface DraftStorytellingChapter {
  id: string;
  name: string;
  colorIndex: number;
  photoMd5s: string[];
}

/**
 * Storytelling data persisted in the draft system.
 */
export interface DraftStorytellingData {
  chapters: DraftStorytellingChapter[];
  config?: { totalSheets: number; sheetsPerChapter: number[] };
  pageChapterIds?: string[];
}

/**
 * Represents standalone product configuration (non-album)
 */
export interface DraftProductConfig {
  productId: number;
  productName?: string;
  productRef?: string;
  categoryId: number;
  categoryName?: string;
  subcategoryId?: number;
  subcategoryName?: string;
  quantity?: number;
  finishValues: DraftFinishValues;
  finishNames?: DraftFinishNames;
  finishFiles?: DraftFinishFiles;
  uploadedPhotos?: DraftFinishFile[];
  panels?: DraftPanelConfig[];
  // Standalone personalization selection state (for BOXES, PARENTS, PROOFS, etc.)
  selectedStandalonePersonalizationType?: number | null;
  /** Multi-select standalone personalization types (max 2). Supersedes the
   *  singular field above (kept for backward-compat with older drafts). */
  selectedStandalonePersonalizationTypes?: number[];
  standalonePersonalizationsOptedOut?: boolean;
  // Child panel personalization selection state (for parent panels like PARENTS_BOX)
  // Key: panelTypeId (as string) for draftVersion >= 2, or panelName for legacy drafts
  selectedChildPanelTypes?: { [panelTypeIdOrName: string]: number[] | number | null };
  childPanelsOptedOut?: { [panelTypeIdOrName: string]: boolean };
  // Main product project data (for standalone products with editor)
  projectId?: number;
  projectServerName?: string;
  projectServerId?: number;
  projectPathStructureId?: number;
  projectThumbnails?: string[]; // Page thumbnails from the editor
  /** ALL editor projects of the main product, in order (first === the
   *  singular fields above, kept for older readers). A standalone draft can
   *  carry several designs of the same product — one order item each. */
  projects?: DraftMainProjectRef[];
  /** "Definir por projeto" mode — designs may override the common finishes. */
  finishOptionsPerProject?: boolean;
}

/** One editor project of a standalone main product (see DraftProductConfig.projects). */
export interface DraftMainProjectRef {
  projectId: number;
  serverName: string;
  serverId?: number;
  pathStructureId?: number;
  thumbnails?: string[];
  /** Per-design finish OVERRIDES (only the options this design changes);
   *  absent = the design uses the product's common finishValues. */
  finishValues?: DraftFinishValues;
  /** Display names of the design's EFFECTIVE finishes (common + overrides). */
  finishNames?: DraftFinishNames;
  /** Design project's server lastModificationDate at snapshot time (parity stamp). */
  projectLastModified?: string;
}

/**
 * Main draft data structure - stores all configuration
 */
export interface DraftData {
  version: string; // Format version for future compatibility
  draftVersion?: number; // 2 = language-independent format (panelTypeId keys), undefined/1 = legacy (panelName keys)
  isAlbum: boolean;
  albumConfig?: DraftAlbumConfig;
  productConfig?: DraftProductConfig;
  createdAt: Date;
  hasFiles?: boolean; // Whether draft includes uploaded files
  totalPrice?: number; // Current total price at time of save (indicative)
  draftThumbnail?: string; // Downscaled cover/first-page thumbnail for drafts list display
}

/**
 * Draft metadata returned from server after save
 */
export interface DraftInfo {
  id: number;
  serverName: string;
  serverId: number;
  pathStructureId: number;
  originalName: string;
  customerId: number;
  productTypeId: number;
  creationDate: string;
  lastModificationDate: string;
  favorite?: number;
  ordered?: number;
  albumProjectId?: number;
  albumProjectServerName?: string;
  totalPrice?: number;
  url?: string;
  thumbnailUrl?: string;
  /** Draft area: 0 = standard, 1 = album design. Server-authoritative (draft_order:read). */
  draftKind?: number;
  /**
   * Inline share summary from /api/drafts. `null` when no share row exists
   * for this draft. Replaces the legacy top-level `draftOrderShareId` field
   * (removed when the summary embed landed server-side).
   */
  share?: { id: number; guestCount: number; approvalStatus: 'pending' | 'approved' } | null;
  /**
   * When a share GUEST last opened this draft (public preview, login or any
   * session request — throttled server-side to 5 min). null = never shared or
   * nobody opened it yet. Owner-only field of /api/drafts (draft_order:read).
   */
  shareGuestLastAccessAt?: string | null;
}

/**
 * Cover personalization overlay for flipbook preview compositing.
 * Represents a layer to be composited over the cover image (stamping, UV, laser, etc.)
 */
export interface EmbossEngraveStyle {
  /** Edge on the light side of the groove — dark on light covers. */
  shadow: string;
  /** Edge opposite the light — catches the light. */
  highlight: string;
  /** Reserved: subtle interior tint — currently unused; the interior stays transparent. */
  recess?: string;
  /** Apparent relief depth, in px at the reference font size (EMBOSSING_FONT_SIZE_PT). */
  offsetPx: number;
}

export interface CoverPersonalizationOverlay {
  type: 'image' | 'text' | 'file';
  panelTypeId: number;
  zIndex: number;
  // For image overlays (UV, Laser, Application)
  imageUrl?: string;
  tintedUrl?: string;
  tintColor?: string;
  /**
   * Corner placement (covers with 3dPreviewType === 3): the compositor draws
   * the artwork cropped to its opaque bounds at the bottom-right corner at
   * this fraction of the canvas instead of the full-cover stretch.
   */
  cornerPlacement?: { sizeFraction: number; marginFraction: number };
  /** Set on plate (chapa) overlays — lets the file layer compose the customer photo INSIDE the plate. */
  plateImageUrl?: string;
  // For text overlays (Stamping)
  text?: string;
  fontFamily?: string;
  fontSizePx?: number;
  color?: string;
  positionClass?: string;
  outline?: boolean;
  outlineOnly?: boolean;
  outlineColor?: string;
  outlineStrokePx?: number;
  /** Two-tone engraving (blind deboss) — when set, renders relief instead of fill/outline. */
  engrave?: EmbossEngraveStyle;
  lineOffsetPx?: number;
  // For file overlays
  fileDataUrl?: string;
}
